<script setup>
    import { computed, reactive, ref } from 'vue'
    import { useNotificationStore } from '@/stores/NotificationStore.js'
    import ListItem from './listItem.vue'

    const notificationStore = useNotificationStore()

    const props = defineProps({
        expertContracts: {
            type: Array,
            required: true
        },
        currentFolderId: {
            type: [Number, String],
            required: false,
            default: null
        }
    })

    const expertContracts = ref(props.expertContracts)
    const listOrder = ref('latest')
    const searchTerm = ref('')
    const contractForDuplication = ref(null)
    const duplicationOptions = reactive({
        title: ''
    })

    const sortOptions = [
        { label: _mft('shared:newestFirst.short'), name: 'latest' },
        { label: _mft('shared:oldestFirst.short'), name: 'oldest' },
    ]

    const loadExpertContracts = async () => {
        let contractRoute = route('expert-contracts.index')
        if (props.currentFolderId) {
            contractRoute = route('expert-contracts.showFolder', props.currentFolderId)
        }

        const response = await axios.get(contractRoute)
        expertContracts.value = response.data.expert_contracts
    }

    const listableExpertContracts = computed(() => {
        const filteredItems = expertContracts.value.filter((expertContract) => {
            return expertContract.title.toLowerCase().includes(searchTerm.value.toLowerCase())
        })

        if (listOrder.value === 'latest') {
            return filteredItems.reverse()
        } else {
            return filteredItems
        }
    })

    const setContractForDuplication = (contract) => {
        contractForDuplication.value = contract
        duplicationOptions.title = contract.title + ' (Copy)'
    }

    const duplicateContract = async () => {
        if (!duplicationOptions.title) {
            return
        }

        try {
            const res = await axios.post(route('expert-contracts.duplicate', contractForDuplication.value.id), {
                title: duplicationOptions.title
            })

            if (res.data.success) {
                notificationStore.addNotification({
                    type: 'success',
                    title: _mft('shared:action.duplicate'),
                    message: res.data.expert_contract_name + ' duplicated successfully',
                    dismissAfter: 5000,
                })
                window.location.href = route('expert-contracts.edit', res.data.expert_contract_id)
            }
        } catch (error) {
            notificationStore.addNotification({
                type: 'error',
                title: _mft('shared:error'),
                message: error.response?.data?.message || 'An error occurred while duplicating',
                dismissAfter: 5000,
            })
        }

        contractForDuplication.value = null
    }

</script>
<template>
    <div>
        <div class="flex justify-between items-center mb-4 @container">
            <div class="flex space-x-2">
                <folders-widget
                    class="min-w-48 bg-white shadow-md rounded-wl"
                    :current-folder-id="currentFolderId"
                    add-route-name="expert-contracts.addFolder"
                    index-route-name="expert-contracts.folders.index"
                    show-route-name="expert-contracts.showFolder"
                ></folders-widget>
                <div class="w-36">
                    <button-bar
                        size="xs"
                        :options="sortOptions"
                        :selected-option-name="listOrder"
                        @select="listOrder = $event.name"
                        :responsive="false"
                        class="!mb-0"
                    />
                </div>
            </div>
            <div>
                <input-text
                    class="!mb-0"
                    :placeholder="_mft('shared:search')"
                    v-model:content="searchTerm"
                />
            </div>
        </div>

        <div class="overflow-hidden bg-white shadow-xl sm:rounded-wl">
            <ul class="divide-y divide-gray-300">
                <li
                    v-for="(expertContract, index) in listableExpertContracts"
                >
                    <ListItem
                        :expert-contract="expertContract"
                        @doRefresh="loadExpertContracts"
                        @open-duplicate="setContractForDuplication"
                    />
                </li>
            </ul>
        </div>

        <slideout-with-slots
            v-if="contractForDuplication !== null"
            @close="contractForDuplication = null"
        >
            <template #title>
                <h2>{{ _mft('shared:action.duplicate') }}</h2>
            </template>
            <template #body>
                <input-text
                    :label="_mft('expertContract:title')"
                    maxlength="255"
                    name="title"
                    placeholder=""
                    required
                    v-model:content="duplicationOptions.title"
                    autofocus
                />
                <div class="flex justify-end mt-4">
                    <button-success
                        :disabled="!duplicationOptions.title"
                        @click="duplicateContract"
                    >
                        {{ _mft('shared:action.duplicate') }}
                    </button-success>
                </div>
            </template>
        </slideout-with-slots>
    </div>
</template>
